import {format, startOfMonth, endOfMonth, eachDayOfInterval} from 'date-fns';
import {Box, getSize} from 'platform/foundation';
import {css, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {createGroupedEventsWithCollisions} from '../utils/createGroupedEventsWithCollisions';
import {RentalEvent} from './RentalEvent';

interface RentalEventRowsProps extends RequiredTestIdProps {
  referenceRef: (element: HTMLElement | null) => void;
  currentDate: Date;
  events: RentalResourceCalendarEvent[];
  hoveredRowIndex: number | null;
  hoveredEvent: RentalResourceCalendarEvent | null;
  isReadOnly?: boolean;
  onEventMouseEnter: (event: RentalResourceCalendarEvent) => void;
  onEventMouseLeave: () => void;
  onRowMouseEnter: (index: number) => void;
  onRowMouseLeave: () => void;
  onEventClick?: (event: RentalResourceCalendarEvent) => void;
}

export function RentalEventRows(props: RentalEventRowsProps) {
  const theme = useTheme();

  const monthStart = startOfMonth(props.currentDate);
  const monthEnd = endOfMonth(props.currentDate);
  const daysInMonth = eachDayOfInterval({start: monthStart, end: monthEnd});
  const groupedEvents = createGroupedEventsWithCollisions(props.currentDate, props.events);
  const emptyRowsCount = Math.max(0, CALENDAR_CONSTANTS.MINIMUM_ROWS - groupedEvents.length);

  const getBorderStyle = (quarterIndex: number, dayIndex: number, daysInMonthLength: number) => {
    const day = daysInMonth[dayIndex];
    const isSunday = format(day, 'i') === '7';
    const isLastDay = dayIndex === daysInMonthLength - 1;

    return match({quarterIndex, isLastDay, isSunday} as const)
      .with({quarterIndex: 0}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with({quarterIndex: 1}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with({quarterIndex: 2}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with({quarterIndex: 3}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with({quarterIndex: 4}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with({quarterIndex: 5}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with({quarterIndex: 6}, () => `1px solid ${theme.colors.palettes.neutral[20][100]}`)
      .with(
        {quarterIndex: 7, isLastDay: false, isSunday: true},
        () => `1px solid ${theme.colors.palettes.neutral[50][100]}`
      )
      .with(
        {quarterIndex: 7, isLastDay: false, isSunday: false},
        () => `1px solid ${theme.colors.general.separator}`
      )
      .otherwise(() => 'none');
  };

  return (
    <div
      css={css`
        height: calc(100% - ${CALENDAR_CONSTANTS.HEADER_HEIGHT}px);
      `}
      data-testid={suffixTestId('eventRows', props)}
    >
      {groupedEvents.map((groupedEvent, groupIndex) => (
        <div
          key={`${groupedEvent.groupId}-${groupedEvent.id}`}
          css={css`
            display: grid;
            grid-template-columns: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH}px 1fr;
            border-bottom: 1px solid ${theme.colors.palettes.neutral[20][100]};
            height: ${groupedEvent.expandedRowHeight}px;
            background-color: ${props.hoveredRowIndex === groupIndex
              ? theme.colors.palettes.neutral[20][100]
              : 'transparent'};
          `}
          data-testid={suffixTestId(`resourceView-row-${groupIndex}`, props)}
          onMouseEnter={() => props.onRowMouseEnter(groupIndex)}
          onMouseLeave={props.onRowMouseLeave}
        >
          <div
            css={css`
              width: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH}px;
              height: ${groupedEvent.expandedRowHeight}px;
              padding: ${getSize(2)};
              border-right: 1px solid ${theme.colors.general.separator};
              visibility: hidden;
            `}
          />

          <Box
            position="relative"
            borderRight="1px solid"
            borderColor="general.separator"
            height="100%"
          >
            <div
              css={css`
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: grid;
                grid-template-columns: repeat(
                  ${daysInMonth.length * 8},
                  ${CALENDAR_CONSTANTS.DAY_COLUMN_WIDTH / 8}px
                );
              `}
            >
              {daysInMonth.flatMap((day, dayIndex) =>
                Array.from({length: 8}, (_, quarterIndex) => (
                  <div
                    key={`${day.toISOString()}-quarter-${quarterIndex}`}
                    css={css`
                      border-right: ${getBorderStyle(quarterIndex, dayIndex, daysInMonth.length)};
                    `}
                  />
                ))
              )}
            </div>

            {groupedEvent.originalEvents.map((originalEvent, eventIndex) => (
              <RentalEvent
                key={originalEvent.id}
                data-testid={props['data-testid']}
                originalEvent={originalEvent}
                eventIndex={eventIndex}
                monthStart={monthStart}
                monthEnd={monthEnd}
                daysInMonthCount={daysInMonth.length}
                collisionLayout={groupedEvent.collisionLayout}
                hoveredEvent={props.hoveredEvent}
                referenceRef={props.referenceRef}
                onEventClick={props.onEventClick}
                onEventMouseEnter={props.onEventMouseEnter}
                onEventMouseLeave={props.onEventMouseLeave}
              />
            ))}
          </Box>
        </div>
      ))}
      {Array.from({length: emptyRowsCount}, (_, index) => index).map((emptyIndex) => {
        const emptyRowIndex = groupedEvents.length + emptyIndex;
        return (
          <div
            key={`resourceView-empty-row-${emptyIndex}`}
            css={css`
              display: grid;
              grid-template-columns: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH}px 1fr;
              border-bottom: 1px solid ${theme.colors.palettes.neutral[20][100]};
              height: ${CALENDAR_CONSTANTS.ROW_HEIGHT}px;
              background-color: ${props.hoveredRowIndex === emptyRowIndex
                ? theme.colors.palettes.neutral[20][100]
                : 'transparent'};
            `}
            data-testid={suffixTestId(`resourceView-empty-row-${emptyIndex}`, props)}
            onMouseEnter={() => props.onRowMouseEnter(emptyRowIndex)}
            onMouseLeave={props.onRowMouseLeave}
          >
            <div
              css={css`
                width: ${CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH}px;
                height: ${CALENDAR_CONSTANTS.ROW_HEIGHT}px;
                padding: ${getSize(2)};
                border-right: 1px solid ${theme.colors.general.separator};
                visibility: hidden;
              `}
            />

            <Box
              position="relative"
              borderRight="1px solid"
              borderColor="general.separator"
              height="100%"
            >
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  display: grid;
                  grid-template-columns: repeat(
                    ${daysInMonth.length * 8},
                    ${CALENDAR_CONSTANTS.DAY_COLUMN_WIDTH / 8}px
                  );
                `}
              >
                {daysInMonth.flatMap((day, dayIndex) =>
                  Array.from({length: 8}, (_, quarterIndex) => (
                    <div
                      key={`${day.toISOString()}-quarter-${quarterIndex}`}
                      css={css`
                        border-right: ${getBorderStyle(quarterIndex, dayIndex, daysInMonth.length)};
                      `}
                    />
                  ))
                )}
              </div>
            </Box>
          </div>
        );
      })}
    </div>
  );
}
